// =============================================================================
// cadGeoref.ts — georeference eligibility + structural validation for kingjson
// drawings, shared between client (map overlay / Show feed) and server (sxworks
// drawing upload-complete).
//
// MapLibre-free and proj4-free on purpose: the Show feed and the server import
// these helpers without pulling the map stack into their bundle. Moved here from
// src/client/map/overlays/cadGeoref.ts in sxworks Phase 2 so the server can
// recompute drawing metadata from the actual uploaded object (client values are
// not trusted).
// =============================================================================

/** Slovenia D96/TM (EPSG:3794) plausibility box — design doc §2 (PostGIS-verified). */
const D96_X_MIN = 370_000;
const D96_X_MAX = 626_500;
const D96_Y_MIN = 30_000;
const D96_Y_MAX = 197_000;

export type LngLat = [number, number];
export type CornerQuad = [LngLat, LngLat, LngLat, LngLat];

function isFiniteNum(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

/** A finite [x, y] tuple (length ≥ 2, first two entries finite). */
function isFiniteTuple(v: unknown): v is [number, number] {
  return Array.isArray(v) && v.length >= 2 && isFiniteNum(v[0]) && isFiniteNum(v[1]);
}

/**
 * A geosx-drawing (V1) entity: the legacy SVG renderer switches on `type` and passes
 * `ent.geometry` to svgLine/svgArc/etc., each of which dereferences geometry fields —
 * so a known type with a missing geometry object (e.g. {type:'LINE'}) crashes the
 * viewer. Require a string `type`, and a geometry object for the 12 rendered types.
 * (Per-field geometry validation is the documented residual — legacy format, the SVG
 * helpers mostly `?? 0`, and a malformed one only breaks the uploader's own view.)
 */
const GEOSX_RENDERED_TYPES: ReadonlySet<string> = new Set([
  'LINE', 'ARC', 'CIRCLE', 'ELLIPSE', 'PLINE', 'POINT',
  'TEXT', 'MTEXT', 'INSERT', 'HATCH', 'SOLID', 'MLEADER',
]);
function isValidGeosxEntity(e: unknown): boolean {
  if (e === null || typeof e !== 'object') return false;
  const ent = e as Record<string, unknown>;
  if (typeof ent.type !== 'string') return false;
  if (GEOSX_RENDERED_TYPES.has(ent.type)) {
    return ent.geometry !== null && typeof ent.geometry === 'object';
  }
  return true; // unknown type → renderer emits a comment, derefs nothing
}

/** An array whose every element is a valid geosx entity (renderer iterates + derefs each). */
function isGeosxEntityArray(v: unknown): boolean {
  return Array.isArray(v) && v.every(isValidGeosxEntity);
}

/** Absent, or a geosx-entity array. Use for OPTIONAL arrays (renderer does `data.x || []`). */
function isGeosxEntityArrayOrAbsent(v: unknown): boolean {
  return v === undefined || isGeosxEntityArray(v);
}

/**
 * A gsx-cad entity must carry the geometry fields the parser/renderer dereference for
 * its type — parse.ts computeBoundingBoxes does `ent.a!`/`ent.e!`/`ent.v!`/`ent.c!`/etc.
 * with non-null assertions, so an entity like {"t":"LN"} (no a/e) crashes the viewer on
 * open. Validate the per-type minimum here. Unknown / H / IN types pass (the renderer
 * guards those or handles them separately).
 */
function isValidEntity(e: unknown): boolean {
  if (e === null || typeof e !== 'object') return false;
  const ent = e as Record<string, unknown>;
  if (typeof ent.t !== 'string') return false;
  switch (ent.t) {
    case 'LN':
      return isFiniteTuple(ent.a) && isFiniteTuple(ent.e);
    case 'PL':
      return Array.isArray(ent.v) && (ent.v as unknown[]).every((n) => isFiniteNum(n));
    case 'C':
    case 'A':
      // radius must be non-negative — GsxCadViewer passes it to ctx.arc(), which throws on r<0.
      return isFiniteTuple(ent.c) && isFiniteNum(ent.r) && (ent.r as number) >= 0;
    case 'EL':
      return isFiniteTuple(ent.c) && isFiniteTuple(ent.maj);
    case 'TX':
    case 'PT':
      return isFiniteTuple(ent.p);
    case 'H':
      // computeBoundingBoxes iterates bnd and dereferences boundary.v; a present-but-
      // malformed bnd (e.g. [{}]) crashes. Absent bnd is fine (the parser guards it).
      return (
        ent.bnd === undefined ||
        (Array.isArray(ent.bnd) &&
          (ent.bnd as unknown[]).every(
            (b) => b !== null && typeof b === 'object' && Array.isArray((b as { v?: unknown }).v)
          ))
      );
    default:
      return true; // IN (handled via blocks) or unknown future type
  }
}

/**
 * If a gsx-cad `spatial` index is present it must be well-formed: parseDrawing does
 * `data.size[0]/cols` and `Object.entries(tiles)`, so a present-but-empty `spatial: {}`
 * (tiles undefined) throws on open. Absent spatial is fine.
 */
function isValidSpatial(spatial: unknown, entityCount: number): boolean {
  if (spatial === undefined) return true;
  if (spatial === null || typeof spatial !== 'object') return false;
  const s = spatial as Record<string, unknown>;
  if (!isFiniteNum(s.cols) || !isFiniteNum(s.rows) || s.tiles === null || typeof s.tiles !== 'object') {
    return false;
  }
  // parseDrawing does Object.entries(tiles) then Object.entries(lodMap), storing each
  // LOD value as the entity-index list; queryVisibleEntities returns those ids and the
  // canvas renderer dereferences model.entities[idx]. Each tile must be a non-null
  // object whose LOD values are arrays of INTEGER indices within entities.length —
  // an out-of-range or fractional id would index a missing entity and break rendering.
  return Object.values(s.tiles as Record<string, unknown>).every(
    (lodMap) =>
      lodMap !== null &&
      typeof lodMap === 'object' &&
      Object.values(lodMap as Record<string, unknown>).every(
        (idx) =>
          Array.isArray(idx) &&
          idx.every((i) => Number.isInteger(i) && (i as number) >= 0 && (i as number) < entityCount)
      )
  );
}

/**
 * gsx-cad `blocks` (if present): parseDrawing.buildBlockPaths iterates
 * `blocks[name].entities`, so a block value of `{}` (entities undefined) throws.
 * Each block must be an object with a valid entities array.
 */
function isValidBlocks(blocks: unknown): boolean {
  if (blocks === undefined) return true;
  if (blocks === null || typeof blocks !== 'object') return false;
  return Object.values(blocks as Record<string, unknown>).every((def) => {
    if (def === null || typeof def !== 'object') return false;
    const entities = (def as { entities?: unknown }).entities;
    if (!Array.isArray(entities) || !entities.every(isValidEntity)) return false;
    // buildBlockPaths also iterates attrDefs and dereferences ad.p[0]/ad.h, so a
    // present attrDefs (optional) must be an array whose entries carry a finite p tuple.
    const attrDefs = (def as { attrDefs?: unknown }).attrDefs;
    if (attrDefs !== undefined) {
      if (!Array.isArray(attrDefs) || !attrDefs.every((ad) => ad !== null && typeof ad === 'object' && isFiniteTuple((ad as { p?: unknown }).p))) {
        return false;
      }
    }
    return true;
  });
}

/**
 * Validate `geo.corners`: exactly four finite `[lng,lat]` pairs in WGS84 range forming a
 * non-degenerate quad. Uploads only check `f==='gsx-cad'`, the server schema is loose, and
 * `parseDrawing` passes `geo` through unchanged — so the contract is enforced HERE.
 * Returns the 4 corners (input order preserved) or null.
 */
export function validCorners(geo: unknown): CornerQuad | null {
  const corners = (geo as { corners?: unknown } | null | undefined)?.corners;
  if (!Array.isArray(corners) || corners.length !== 4) return null;
  const pts: LngLat[] = [];
  for (const item of corners as unknown[]) {
    if (!Array.isArray(item) || item.length < 2) return null;
    const lng: unknown = (item as unknown[])[0];
    const lat: unknown = (item as unknown[])[1];
    if (!isFiniteNum(lng) || !isFiniteNum(lat)) return null;
    if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return null;
    pts.push([lng, lat]);
  }
  const [a, b, c, d] = pts;
  if (!a || !b || !c || !d) return null;
  // Non-degenerate: shoelace area must be meaningfully positive.
  const area =
    Math.abs(
      a[0] * b[1] - b[0] * a[1] +
        b[0] * c[1] - c[0] * b[1] +
        c[0] * d[1] - d[0] * c[1] +
        d[0] * a[1] - a[0] * d[1],
    ) / 2;
  if (area < 1e-12) return null;
  return [a, b, c, d];
}

function originInD96Bounds(origin: unknown): boolean {
  if (!Array.isArray(origin)) return false;
  const x: unknown = (origin as unknown[])[0];
  const y: unknown = (origin as unknown[])[1];
  return (
    isFiniteNum(x) && isFiniteNum(y) &&
    x >= D96_X_MIN && x <= D96_X_MAX &&
    y >= D96_Y_MIN && y <= D96_Y_MAX
  );
}

/**
 * A drawing is map-eligible iff it has valid `geo.corners` OR its `origin` is a plausible
 * D96/TM coordinate in Slovenia. Accepts either the raw gsx-cad JSON or a parsed model
 * (both expose `origin` and `geo`). Excludes local EK schematics (`origin:[0,0]`, no corners)
 * and rejects nonzero-but-non-D96 origins that would otherwise fly the map to a wrong place.
 */
export function isGeoreferenced(json: { origin?: unknown; geo?: unknown }): boolean {
  if (validCorners(json?.geo)) return true;
  return originInD96Bounds(json?.origin);
}

// =============================================================================
// Structural validation (sxworks Phase 2)
// =============================================================================

/**
 * Formats a doc_type='drawing' row may carry. 'pdf' is a načrt technical report
 * (#649): stored under the same doc_type so every works-file guard applies to it;
 * never map-eligible, never accepted by validateDrawingPayload (JSON only).
 */
export type DrawingFormat = 'gsx-cad' | 'geosx-drawing' | 'pdf';

/** D96/TM extent [minx, miny, maxx, maxy] in EPSG:3794. */
export type Extent3794 = [number, number, number, number];

export interface DrawingMeta {
  format: DrawingFormat;
  georeferenced: boolean;
  /**
   * #649: the drawing's D96 extent (origin + size), stored at completion ONLY when
   * finite and inside the D96 plausibility box — an unbounded envelope makes
   * ST_Transform raise »outside of projection domain« and would fail the načrti
   * list that compares it with the graditev boundary. Absent for corner-georeferenced
   * drawings and for anything out of bounds.
   */
  extent3794?: Extent3794;
}

/**
 * The D96 extent of a gsx-cad drawing from `origin` + `size`, or null when any
 * corner is not finite or falls outside the D96 plausibility box (see DrawingMeta).
 */
export function drawingExtent3794(json: { origin?: unknown; size?: unknown }): Extent3794 | null {
  const origin = json?.origin;
  const size = json?.size;
  if (!isFiniteTuple(origin) || !isFiniteTuple(size)) return null;
  const [ox, oy] = origin;
  const [w, h] = size;
  if (w <= 0 || h <= 0) return null;
  const maxx = ox + w;
  const maxy = oy + h;
  if (!Number.isFinite(maxx) || !Number.isFinite(maxy)) return null;
  if (!originInD96Bounds([ox, oy]) || !originInD96Bounds([maxx, maxy])) return null;
  return [ox, oy, maxx, maxy];
}

export type DrawingValidation =
  | { ok: true; meta: DrawingMeta }
  | { ok: false; error: string };

/**
 * Validate a parsed drawing payload STRUCTURALLY (not just the format marker) and
 * derive its trustworthy metadata. A marker-only `{f:'gsx-cad', origin:[…]}` passes
 * `isGeoreferenced` but crashes `parseDrawing` on `data.layers.map` — so the renderer's
 * required shape is checked here. Used by BOTH the client upload flow (pre-PUT reject)
 * and the server complete handler (recompute from the actual S3 object — the client's
 * own `drawing_meta` is never trusted). Returns the canonical `{format, georeferenced}`.
 */
export function validateDrawingPayload(data: unknown): DrawingValidation {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return { ok: false, error: 'Datoteka ni veljaven JSON-objekt risbe.' };
  }
  const d = data as Record<string, unknown>;

  // V2 gsx-cad — must carry the fields parseDrawing/the renderer dereference.
  if (d.f === 'gsx-cad') {
    // Element-shape checks too, not just "is an array": parseDrawing iterates these and
    // dereferences each entry, so a null/primitive element (e.g. entities:[null]) would
    // crash the viewer on open — reject it here so a completed drawing always opens.
    if (!Array.isArray(d.layers) || !d.layers.every((l) => typeof l === 'string')) {
      return { ok: false, error: 'gsx-cad risba nima veljavnega polja "layers".' };
    }
    if (!Array.isArray(d.styles) || !d.styles.every((s) => s !== null && typeof s === 'object')) {
      return { ok: false, error: 'gsx-cad risba nima veljavnega polja "styles".' };
    }
    if (!Array.isArray(d.entities) || !d.entities.every(isValidEntity)) {
      return { ok: false, error: 'gsx-cad risba nima veljavnega polja "entities".' };
    }
    if (!isFiniteTuple(d.origin)) return { ok: false, error: 'gsx-cad risba nima veljavnega "origin".' };
    // size must be a positive tuple — the viewer/overlay divide by width/height, so a
    // zero/negative dimension produces a broken (NaN/Infinity) render.
    if (!isFiniteTuple(d.size) || d.size[0] <= 0 || d.size[1] <= 0) {
      return { ok: false, error: 'gsx-cad risba nima veljavnega "size".' };
    }
    if (!isValidSpatial(d.spatial, (d.entities as unknown[]).length)) {
      return { ok: false, error: 'gsx-cad risba ima neveljaven "spatial" indeks.' };
    }
    if (!isValidBlocks(d.blocks)) {
      return { ok: false, error: 'gsx-cad risba ima neveljavne "blocks".' };
    }
    return { ok: true, meta: { format: 'gsx-cad', georeferenced: isGeoreferenced(d) } };
  }

  // V1 geosx-drawing — legacy SVG renderer; never map-georeferenced (overlay is gsx-cad only).
  // viewBox is the renderer's `DrawingViewBox` shape: {minX, minY, width, height}. The
  // renderer (geosx-drawing-svg-renderer.ts calculateViewBox) only requires finite,
  // positive width/height (minX/minY default to 0), so mirror exactly that gate.
  if (d.format === 'geosx-drawing') {
    const vb = d.viewBox as Record<string, unknown> | undefined;
    if (
      !vb ||
      typeof vb !== 'object' ||
      !isFiniteNum(vb.width) || !isFiniteNum(vb.height) ||
      vb.width <= 0 || vb.height <= 0
    ) {
      return { ok: false, error: 'geosx-drawing risba nima veljavnega "viewBox".' };
    }
    // buildDrawingSVG iterates entities / blockDefs[*].entities / features[*].entities and
    // calls entityToSVG on each, so a present-but-malformed (null/primitive) element crashes
    // /drawing-viewer. Element-shape-check the arrays the renderer dereferences.
    if (!isGeosxEntityArrayOrAbsent(d.entities)) {
      return { ok: false, error: 'geosx-drawing risba ima neveljavno polje "entities".' };
    }
    // blockDefs / features each iterate `.entities` unguarded → entities is REQUIRED
    // (an array of entities), unlike the optional top-level `entities` (renderer: `||[]`).
    if (d.blockDefs !== undefined) {
      if (
        d.blockDefs === null ||
        typeof d.blockDefs !== 'object' ||
        !Object.values(d.blockDefs as Record<string, unknown>).every(
          (b) => b !== null && typeof b === 'object' && isGeosxEntityArray((b as { entities?: unknown }).entities)
        )
      ) {
        return { ok: false, error: 'geosx-drawing risba ima neveljavne "blockDefs".' };
      }
    }
    if (d.features !== undefined) {
      if (
        !Array.isArray(d.features) ||
        !d.features.every(
          (f) => f !== null && typeof f === 'object' && isGeosxEntityArray((f as { entities?: unknown }).entities)
        )
      ) {
        return { ok: false, error: 'geosx-drawing risba ima neveljavno polje "features".' };
      }
    }
    return { ok: true, meta: { format: 'geosx-drawing', georeferenced: false } };
  }

  return { ok: false, error: 'Neveljavna oblika datoteke (pričakovano: gsx-cad ali geosx-drawing).' };
}
